import { Avatar, AVATAR_SIZES } from "@neru/base/components";
import { config } from "@neru/base/config";
import {
  useCurrentAccount,
  useNavigateTo,
  useValidSession,
} from "@neru/base/hooks";
import * as S from "@neru/base/styles";
import { ProductType_Enum, STATUS_TO_CONNOTATION_MAP } from "@neru/base/types";
import { getAccountId, Text } from "@neru/base/utils";
import type { Connotations, WithChildren } from "@neru/types";
import {
  Badge,
  Card,
  H3,
  Icon,
  type IconType,
  Menu,
  Phrase,
  Tooltip,
} from "@neru/ui";

import type { GetProductsResponse } from "./hooks";

interface ProductCardProps {
  product: GetProductsResponse["data"]["Products"][number];
}

interface WrapperProps extends WithChildren {
  isLive: boolean;
  isPublic: boolean;
}

enum PRODUCT_ACTIONS {
  manageVersions = "MANAGE_VERSIONS",
}

const PRODUCT_OPTIONS: Array<{
  action: PRODUCT_ACTIONS;
  connotation: Connotations;
  icon: IconType;
  title: string;
}> = [
  {
    action: PRODUCT_ACTIONS.manageVersions,
    connotation: "primary",
    icon: "list-line",
    title: "Manage versions",
  },
];

const Wrapper = ({ children, isLive, isPublic }: WrapperProps) =>
  isLive && isPublic ? (
    <>{children}</>
  ) : (
    <Tooltip mouseEnterDelay={0.5} text="This product is only visible to you.">
      {children}
    </Tooltip>
  );

export const ProductCard = ({ product }: ProductCardProps) => {
  const { canPublishProducts } = useCurrentAccount();
  const navigateTo = useNavigateTo();

  const {
    icon,
    id,
    isLive,
    isPublic,
    LatestVersion,
    name,
    owner,
    owner_name,
    short_description,
    tags,
    type,
  } = product;

  const lastVersion = (LatestVersion ?? [])[0];
  const isProductOwner = getAccountId() === owner;

  const navigateToProductOverview = () => {
    navigateTo("/:id", { id, name });
  };

  const onMenuOptionSelected = async (action: PRODUCT_ACTIONS) => {
    switch (action) {
      case PRODUCT_ACTIONS.manageVersions:
        navigateTo("/:id/versions", { id, name });
    }
  };

  const getProductTypeIcon = () => {
    if (type === ProductType_Enum.Private)
      return (
        <Tooltip placement="bottom" text="Private product">
          <Icon connotation="info" size="small" type="lock-solid" />
        </Tooltip>
      );

    if (type === ProductType_Enum.Unlisted)
      return (
        <Tooltip placement="bottom" text="Unlisted product">
          <Icon connotation="info" size="small" type="eye-hide-solid" />
        </Tooltip>
      );

    return null;
  };

  return (
    <Wrapper isLive={isLive} isPublic={isPublic}>
      <Card
        className="menu-lift-up mb-4 h-[380px]"
        data-testid={`product-card-${id}`}
        onClick={navigateToProductOverview}
      >
        <S.Column height="100%">
          <S.Row justifyContent="space-between">
            <S.Column>
              <Avatar
                fallbackImage={`${
                  import.meta.env.VITE_PUBLIC_URL
                }/img/no-product-image.png`}
                size={AVATAR_SIZES.large}
                src={`${config.marketCDNUrl}/${icon}`}
                title={name}
              />
            </S.Column>

            {useValidSession() && isProductOwner && (
              <S.Column>
                <S.Row flexWrap="nowrap">
                  {!isPublic && (
                    <S.Column marginRight="sm" style={{ whiteSpace: "nowrap" }}>
                      <S.Row flexWrap="nowrap">{getProductTypeIcon()}</S.Row>
                    </S.Column>
                  )}

                  {!isLive && lastVersion?.status ? (
                    <span
                      style={{
                        position: "absolute",
                        right: "16px",
                        top: "-8px",
                      }}
                    >
                      <Badge
                        connotation={
                          STATUS_TO_CONNOTATION_MAP[lastVersion.status]
                        }
                        data-testid={`status-version-${lastVersion.version}`}
                        layout="soft"
                      >
                        {Text.toSentenceCase(lastVersion.status, true)}
                      </Badge>
                    </span>
                  ) : null}

                  {canPublishProducts && (
                    <S.Column alignItems="flex-end" flexGrow={1}>
                      <Menu
                        data-testid="product-card-menu"
                        icon="more-vertical-line"
                        menuItems={PRODUCT_OPTIONS}
                        onAction={onMenuOptionSelected}
                      />
                    </S.Column>
                  )}
                </S.Row>
              </S.Column>
            )}
          </S.Row>
          <S.Row marginBottom="lg" marginTop="md">
            <S.Column flexGrow={1} justifyContent="space-between">
              <H3 className="mb-2 mt-1 break-words leading-8">
                {Text.truncateStringByWord(name, 65)}
              </H3>
              <Phrase className="mb-2 text-primary-500">{owner_name}</Phrase>
              <Phrase className="mt-2 line-clamp-3 text-sm">
                {short_description}
              </Phrase>
            </S.Column>
          </S.Row>
          <S.Row marginTop="auto">
            <S.Column flexGrow={1}>
              <S.Row>
                {tags.map((tag: string) => (
                  <Badge className="mr-1 mt-1" key={tag} layout="soft">
                    {tag}
                  </Badge>
                ))}
              </S.Row>
            </S.Column>
          </S.Row>
        </S.Column>
      </Card>
    </Wrapper>
  );
};
